import { getDayJs } from '~/services/TimeService';

import type { ReviewStatus } from './reviewTypes';
import type { CommandDeparture, CostBasis } from './types';

export const reviewStatuses: Array<ReviewStatus> = [
  'Urgent',
  'Needs Action',
  'Assessment Incomplete',
  'Watch',
  'Monitor',
  'On Track',
  'Final',
];
export const reviewOf = (d: CommandDeparture, basis: CostBasis) => d[`review_${basis}`];
export const statusOf = (d: CommandDeparture, basis: CostBasis): ReviewStatus | null =>
  reviewOf(d, basis)?.review_status ?? d.review_status ?? null;
export const inReview = (d: CommandDeparture) =>
  d.in_review_window ??
  (d.days_to_handback >= 0 && d.days_to_handback <= 240 && ((d.on_sale ?? d.state === 'On sale') || d.pax > 0));
export function daysAtStatus(d: CommandDeparture, basis: CostBasis, today: string) {
  const since = reviewOf(d, basis)?.status_since ?? d.status_since;
  return since ? Math.max(0, getDayJs(today).startOf('day').diff(getDayJs(since).startOf('day'), 'day')) : null;
}
export function statusTooltip(d: CommandDeparture, basis: CostBasis) {
  const review = reviewOf(d, basis);
  const status = statusOf(d, basis);
  if (!status) {
    return 'Review assessment missing from this older snapshot.';
  }
  const reasons = review?.review_reasons ?? d.review_reasons ?? [];
  const since = review?.status_since ?? d.status_since;
  const previous = review?.previous_status ?? d.previous_status;
  return `${status}${reasons.length ? ` (${reasons.map((r) => r.text).join('; ')})` : ''}${since ? ` since ${getDayJs(since).format('D MMM')}.` : '.'}${previous ? ` Was ${previous} before that.` : ''}`;
}
export function dataAsOf(generatedAt?: string | null) {
  if (!generatedAt) {
    return 'Data refresh time missing from this older snapshot.';
  }
  const date = new Date(generatedAt);
  if (!Number.isFinite(date.getTime())) {
    return 'Data refresh time missing.';
  }
  const day = date.toLocaleDateString('en-AU', {
    timeZone: 'Australia/Sydney',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const time = date
    .toLocaleTimeString('en-AU', { timeZone: 'Australia/Sydney', hour: 'numeric', minute: '2-digit', hour12: true })
    .replace(/\s/g, '')
    .toLowerCase();
  return `Data as of ${day}, ${time} Sydney`;
}
export const rmStatus = (d: Pick<CommandDeparture, 'price_level_now' | 'price_level_mixed'>) =>
  d.price_level_mixed
    ? 'Mixed'
    : d.price_level_now == null
      ? 'Price level missing'
      : d.price_level_now > 0
        ? 'Yield'
        : d.price_level_now < 0
          ? 'Discount'
          : 'Base';
export function lastPriceAction(value: unknown): string | null {
  let action: { status?: string; applied_percent?: number; created_at?: string; created_by_username?: string };
  try {
    action = typeof value === 'string' ? JSON.parse(value) : value;
  } catch {
    return null;
  }
  if (!action?.status || !action.created_at) {
    return null;
  }
  const date = getDayJs(action.created_at).format('D MMM');
  const percent =
    action.applied_percent == null
      ? ''
      : `${action.applied_percent >= 0 ? '+' : '−'}${Math.abs(action.applied_percent * 100).toLocaleString('en-AU')}% `;
  return `Last price action: ${action.status === 'applied' || action.status === 'accepted' ? `${percent}applied` : action.status} ${date}${action.created_by_username ? ` by ${action.created_by_username}` : ''}`;
}
export const queueCodes: Record<string, Array<string>> = {
  Pricing: ['A01', 'A02', 'A12'],
  Marketing: ['A04'],
  'Hide or supply': ['A09', 'A14'],
  'Data issues': ['A15'],
  Monitor: ['A16'],
};

export const reviewMoney = (value: number | null | undefined) =>
  value == null
    ? 'Information missing'
    : value.toLocaleString('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
export const reviewPercent = (value: number | null | undefined) =>
  value == null ? 'Information missing' : `${(value * 100).toFixed(2)}%`;
